/** @jsx jsx */
import type * as React from 'react'
import styled from '@emotion/styled/macro'
import { css, jsx } from '@emotion/react'

// Props
export interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string

  /**
   * ボタンの形状
   * @default rounded
   */
  shape?: 'rounded' | 'circle'

  /**
   * カラーテーマ
   * @default white
   */
  colorTheme?:
    | 'white'
    | 'blue'
    | 'blue-outline'
    | 'teal'
    | 'teal-outline'
    | 'red'

  /**
   * ボタンが有効かどうか
   * @default false
   */
  disabled?: boolean

  /**
   * ボタンを全幅にするかどうか
   * @default false
   */
  fullWidth?: boolean

  /**
   * ボタンのサイズ
   * @default medium
   */
  size?: 'small' | 'medium'

  /** クリックイベント */
  onClick?: React.MouseEventHandler<HTMLButtonElement>

  /** 視覚的無効化（クリックは通す） */
  'aria-disabled'?: boolean | 'true' | 'false' | undefined
}

// JSX
/**
 * 汎用的に使用するアウトラインスタイルのボタン
 */
export const OutlineButton: React.FC<Props> = ({
  id,
  children,
  label,
  className,
  shape = 'rounded',
  colorTheme = 'white',
  disabled,
  fullWidth,
  size = 'medium',
  onClick,
  'aria-disabled': ariaDisabled,
  ...rest
}) => {
  const StyledButton =
    shape === 'rounded'
      ? size === 'small'
        ? RoundedSmall
        : Rounded
      : size === 'small'
      ? CircleSmall
      : Circle

  const color = () => {
    switch (colorTheme) {
      case 'blue':
        return 'var(--white)'
      case 'white':
        return 'var(--black)'
      case 'blue-outline':
        return 'var(--primary-color-dark)'
      case 'teal':
        return 'var(--gray-0)'
      case 'teal-outline':
        return 'var(--teal-400)'
      case 'red':
        return 'var(--gray-0)'
    }
  }

  const background = () => {
    switch (colorTheme) {
      case 'blue':
        return 'var(--primary-color)'
      case 'white':
      case 'blue-outline':
        return 'var(--white)'
      case 'teal':
        return 'var(--teal-400)'
      case 'teal-outline':
        return 'var(--white)'
      case 'red':
        return 'var(--red-400)'
    }
  }

  const borderColor = () => {
    switch (colorTheme) {
      case 'blue':
        return 'transparent'
      case 'white':
        return '#cfcfcf'
      case 'blue-outline':
        return 'var(--primary-color)'
      case 'teal':
        return 'var(--teal-400)'
      case 'teal-outline':
        return 'var(--teal-400)'
      case 'red':
        return 'var(--red-400)'
    }
  }

  const backgroundHover = () => {
    switch (colorTheme) {
      case 'blue':
        return 'var(--primary-color-dark)'
      case 'white':
        return '#ededed'
      case 'blue-outline':
        return '#ededed'
      case 'teal':
        return 'var(--teal-100)'
      case 'teal-outline':
        return 'var(--teal-50)'
      case 'red':
        return 'var(--red-100)'
    }
  }

  return (
    <StyledButton
      id={id}
      className={className}
      css={css(base, {
        color: color(),
        background: background(),
        borderColor: borderColor(),
        width: fullWidth ? '100%' : 'auto',
        '&:hover': {
          background: backgroundHover()
        },
        '&:disabled': {
          opacity: 0.5,
          cursor: 'not-allowed',
          '&:hover': {
            background: background()
          }
        },
        "&[aria-disabled='true']": {
          opacity: 0.5,
          cursor: 'not-allowed'
        }
      })}
      title={label}
      aria-label={label}
      disabled={disabled}
      aria-disabled={ariaDisabled ? 'true' : undefined}
      onClick={onClick}
      {...rest}
    >
      {children}
    </StyledButton>
  )
}

export default OutlineButton

const base = css`
  appearance: none;
  border-width: 1px;
  border-style: solid;
  box-sizing: border-box;
  text-align: center;
  transition-property: background-color;
  transition-duration: 0.2s;
  transition-timing-function: ease;
  min-height: 44px;
  min-width: 44px;
`

const Rounded = styled.button`
  display: inline-block;
  padding: var(--gutter-small);
  border-radius: 50px;
`

const RoundedSmall = styled.button`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
`

const Circle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  padding: 0;
  border-radius: 50%;

  & svg {
    width: 20px;
    height: 20px;
  }
`

const CircleSmall = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  padding: 0;
  border-radius: 50%;
  font-size: 14px;

  & svg {
    width: 14px;
    height: 14px;
  }
`
